/**
 * Section Divider — Frontend Injection
 *
 * Paste this into your assets/src/js/global.js.
 *
 * Reads `data-section-divider` JSON from each .has-section-divider element
 * and injects the appropriate SVG divider HTML — exactly mirroring what the
 * editor preview renders in the canvas.
 *
 * SVG_PATHS here must stay in sync with SVG_PATHS in blocks/section-divider/index.js.
 */

const SVG_PATHS = {
  wave: "M0,40 C200,100 400,0 600,50 C800,100 1000,10 1200,50 L1200,120 L0,120 Z",

  zigzag:
    "M0,60 L50,0 L100,60 L150,0 L200,60 L250,0 L300,60 L350,0 L400,60 " +
    "L450,0 L500,60 L550,0 L600,60 L650,0 L700,60 L750,0 L800,60 L850,0 " +
    "L900,60 L950,0 L1000,60 L1050,0 L1100,60 L1150,0 L1200,60 L1200,120 L0,120 Z",

  slant: "M0,0 L1200,80 L1200,120 L0,120 Z",

  torn:
    "M0,25 L18,8 L35,28 L55,5 L72,22 L90,10 L108,30 L125,8 L142,25 " +
    "L160,5 L178,20 L195,10 L215,32 L232,12 L250,28 L268,5 L285,22 " +
    "L303,12 L320,30 L338,8 L356,25 L373,5 L390,22 L408,15 L426,30 " +
    "L443,8 L460,28 L478,5 L496,25 L513,10 L530,30 L548,8 L565,22 " +
    "L583,10 L600,28 L618,5 L636,22 L653,15 L670,30 L688,5 L706,25 " +
    "L723,10 L740,28 L758,5 L776,22 L793,15 L810,30 L828,8 L846,25 " +
    "L863,5 L880,28 L898,12 L916,25 L933,8 L950,30 L968,5 L986,22 " +
    "L1003,10 L1020,28 L1038,15 L1056,5 L1073,25 L1090,10 L1108,30 " +
    "L1126,8 L1143,22 L1160,5 L1178,28 L1200,15 L1200,120 L0,120 Z",

  clouds:
    "M0,60 C20,60 20,25 40,25 C60,25 60,60 80,60 C100,60 100,25 120,25 " +
    "C140,25 140,60 160,60 C180,60 180,25 200,25 C220,25 220,60 240,60 " +
    "C260,60 260,25 280,25 C300,25 300,60 320,60 C340,60 340,25 360,25 " +
    "C380,25 380,60 400,60 C420,60 420,25 440,25 C460,25 460,60 480,60 " +
    "C500,60 500,25 520,25 C540,25 540,60 560,60 C580,60 580,25 600,25 " +
    "C620,25 620,60 640,60 C660,60 660,25 680,25 C700,25 700,60 720,60 " +
    "C740,60 740,25 760,25 C780,25 780,60 800,60 C820,60 820,25 840,25 " +
    "C860,25 860,60 880,60 C900,60 900,25 920,25 C940,25 940,60 960,60 " +
    "C980,60 980,25 1000,25 C1020,25 1020,60 1040,60 C1060,60 1060,25 " +
    "1080,25 C1100,25 1100,60 1120,60 C1140,60 1140,25 1160,25 " +
    "C1180,25 1180,60 1200,60 L1200,120 L0,120 Z",
};

function initSectionDividers() {
  document
    .querySelectorAll(".has-section-divider[data-section-divider]")
    .forEach((el) => {
      // Skip if already initialised (handles dynamic content re-render)
      if (el.dataset.dividerInit) return;
      el.dataset.dividerInit = "1";

      let data;
      try {
        data = JSON.parse(el.dataset.sectionDivider);
      } catch {
        return;
      }

      // Ensure wrapper is positioned so absolute children anchor correctly
      const currentPosition = window.getComputedStyle(el).position;
      if (currentPosition === "static") {
        el.style.position = "relative";
      }

      ["top", "bottom"].forEach((side) => {
        if (!data[side]) return;

        const { type, color, height, flipH, position } = data[side];
        const path = SVG_PATHS[type] || SVG_PATHS.wave;

        // ── CSS transform ────────────────────────────────────────────────
        const transforms = [];
        if (side === "top") transforms.push("rotate(180deg)");
        if (flipH) transforms.push("scaleX(-1)");
        const transform = transforms.length ? transforms.join(" ") : "none";

        // ── Overflow for clip/overlap mode ───────────────────────────────
        if (position === "clip") {
          el.style.overflow = "hidden";
        } else {
          el.style.overflow = "visible";
        }

        // ── Build divider element ────────────────────────────────────────
        const wrapper = document.createElement("div");
        wrapper.setAttribute("aria-hidden", "true");
        wrapper.className = [
          "section-divider",
          `section-divider--${side}`,
          `section-divider--${position}`,
          flipH ? "section-divider--flip-h" : "",
        ]
          .filter(Boolean)
          .join(" ");

        Object.assign(wrapper.style, {
          position: "absolute",
          [side]: "0",
          left: "0",
          width: "100%",
          height: `${height}px`,
          transform,
          transformOrigin: "center",
          pointerEvents: "none",
          lineHeight: "0",
          zIndex: "2",
        });

        wrapper.innerHTML = `
                <svg xmlns="http://www.w3.org/2000/svg"
                     viewBox="0 0 1200 120"
                     preserveAspectRatio="none"
                     style="display:block;width:100%;height:100%;">
                    <path d="${path}" fill="${CSS.escape ? color : color}" />
                </svg>`;

        el.appendChild(wrapper);
      });
    });
}

// Run on DOM ready
document.addEventListener("DOMContentLoaded", initSectionDividers);
